/* adt */
import compact, { Compact } from './adt/compact';
import each, { Each } from './adt/each';
import every, { Every } from './adt/every';
import filter, { Filter } from './adt/filter';
import find, { Find } from './adt/find';
import keys, { Keys } from './adt/keys';
import group, { Group } from './adt/group';
import map, { Map } from './adt/map';
import partition, { Partition } from './adt/partition';
import reduce, { Reduce } from './adt/reduce';
import remove, { Remove } from './adt/remove';
import size, { Size } from './adt/size';
import some, { Some } from './adt/some';
import traverse, { Traverse } from './adt/traverse';
import source, { Source } from './adt/source';
import unique, { Unique } from './adt/unique';
import unpartition, { Unpartition } from './adt/unpartition';
import ungroup, { Ungroup } from './adt/ungroup';
import until, { Until } from './adt/until';
import values, { Values } from './adt/values';
import iterable, { Iterable } from './adt/iterable';

/* array */
import bounds, { Bounds } from './array/bounds';
import chunk, { Chunk } from './array/chunk';
import concat, { Concat } from './array/concat';
import dequeue, { Dequeue } from './array/dequeue';
import difference, { Difference } from './array/difference';
import enqueue, { Enqueue } from './array/enqueue';
import flatten, { Flatten } from './array/flatten';
import head, { Head } from './array/head';
import initial, { Initial } from './array/initial';
import intersection, { Intersection } from './array/intersection';
import last, { Last } from './array/last';
import nth, { Nth } from './array/nth';
import pop, { Pop } from './array/pop';
import push, { Push } from './array/push';
import range, { Range } from './array/range';
import reverse, { Reverse } from './array/reverse';
import segment, { Segment } from './array/segment';
import slice, { Slice } from './array/slice';
import sort, { Sort } from './array/sort';
import tail, { Tail } from './array/tail';
import union, { Union } from './array/union';

/* asynchronous */
import parallel, { Parallel } from './async/parallel';
import serial, { Serial } from './async/serial';
import tasks, { Tasks } from './async/tasks';
import task, { Task } from './async/task';
import done, { Done } from './async/done';

/* boolean */
import and, { And } from './bln/and';
import either, { Either } from './bln/either';
import eq, { Eq } from './bln/eq';
import gt, { Gt } from './bln/gt';
import gte, { Gte } from './bln/gte';
import lt, { Lt } from './bln/lt';
import lte, { Lte } from './bln/lte';
import nand, { Nand } from './bln/nand';
import nor, { Nor } from './bln/nor';
import not, { Not } from './bln/not';
import or, { Or } from './bln/or';
import xor, { Xor } from './bln/xor';

/* database */
import write, { Write } from './db/write';
import read, { Read } from './db/read';
import lens, { Lens } from './db/lens';

/* function */
import always, { Always } from './fn/always';
import c2, { Curry2 } from './fn/c2';
import c3, { Curry3 } from './fn/c3';
import c4, { Curry4 } from './fn/c4';
import c5, { Curry5 } from './fn/c5';
import cN, { CurryN } from './fn/cn';
import compose, { Compose } from './fn/compose';
import conditional, { Conditional } from './fn/conditional';
import converge, { Converge } from './fn/converge';
import debounce, { Debounce } from './fn/debounce';
import delay, { Delay } from './fn/delay';
import identity, { Identity } from './fn/identity';
import lift, { Lift } from './fn/lift';
import memoize, { Memoize } from './fn/memoize';
import method, { Method } from './fn/method';
import mimic, { Mimic } from './fn/mimic';
import noop, { Noop } from './fn/noop';
import once, { Once } from './fn/once';
import pipe, { Pipe } from './fn/pipe';
import safe, { Safe } from './fn/safe';
import skip, { Skip } from './fn/skip';
import strategy, { Strategy } from './fn/strategy';
import tap, { Tap } from './fn/tap';

/* is */
import array, { IsArray } from './is/is_array';
import between, { IsBetween } from './is/is_between';
import boolean, { IsBoolean } from './is/is_boolean';
import bounded, { IsBounded } from './is/is_bounded';
import char, { IsChar } from './is/is_char';
import is_class, { IsClass } from './is/is_class';
import date, { IsDate } from './is/is_date';
import is_elements, { IsElements } from './is/is_elements';
import empty, { IsEmpty } from './is/is_empty';
import equal, { IsEqual } from './is/is_equal';
import equal_deep, { IsEqualDeep } from './is/is_equal_deep';
import error, { IsError } from './is/is_error';
import even, { IsEven } from './is/is_even';
import exists, { IsExists } from './is/is_exists';
import is_false, { IsFalse } from './is/is_false';
import falsy, { IsFalsy } from './is/is_falsy';
import is_function, { IsFunction } from './is/is_function';
import is_generator, { IsGenerator } from './is/is_generator';
import is_in, { IsIn } from './is/is_in';
import integer, { IsInteger } from './is/is_integer';
import is_iterable, { IsIterable } from './is/is_iterable';
import is_iterator, { IsIterator } from './is/is_iterator';
import json, { IsJson } from './is/is_json';
import is_like, { IsLike } from './is/is_like';
import is_lowercase, { IsLowercase } from './is/is_lowercase';
import is_map, { IsMap } from './is/is_map';
import nan, { IsNan } from './is/is_nan';
import negative, { IsNegative } from './is/is_negative';
import nil, { IsNil } from './is/is_nil';
import is_null, { IsNull } from './is/is_null';
import number, { IsNumber } from './is/is_number';
import object, { IsObject } from './is/is_object';
import odd, { IsOdd } from './is/is_odd';
import pattern, { IsPattern } from './is/is_pattern';
import record, { IsRecord } from './is/is_record';
import positive, { IsPositive } from './is/is_positive';
import is_private, { IsPrivate } from './is/is_private';
import is_public, { IsPublic } from './is/is_public';
import is_protected, { IsProtected } from './is/is_protected';
import is_promise, { IsPromise } from './is/is_promise';
import real, { IsReal } from './is/is_real';
import regexp, { IsRegExp } from './is/is_regexp';
import is_set, { IsSet } from './is/is_set';
import is_similar, { IsSimilar } from './is/is_similar';
import is_string, { IsString } from './is/is_string';
import symbol, { IsSymbol } from './is/is_symbol';
import is_true, { IsTrue } from './is/is_true';
import truthy, { IsTruthy } from './is/is_truthy';
import is_undefined, { IsUndefined } from './is/is_undefined';
import is_uppercase, { IsUppercase } from './is/is_uppercase';
import value, { IsValue } from './is/is_value';

/* number */
import add, { Add } from './num/add';
import ascending, { Ascending } from './num/ascending';
import ceiling, { Ceiling } from './num/ceiling';
import clamp, { Clamp } from './num/clamp';
import dec, { Dec } from './num/dec';
import digits, { Digits } from './num/digits';
import divide, { Divide } from './num/divide';
import fraction, { Fraction } from './num/fraction';
import inc, { Inc } from './num/inc';
import max, { Max } from './num/max';
import min, { Min } from './num/min';
import modulo, { Modulo } from './num/modulo';
import multiply, { Multiply } from './num/multiply';
import precision, { Precision } from './num/precision';
import random, { Random } from './num/random';
import sign, { Sign } from './num/sign';
import subtract, { Subtract } from './num/subtract';

/* record */
import assign, { Assign } from './record/assign';
import clone, { Clone } from './record/clone';
import copy, { Copy } from './record/copy';
import key, { Key } from './record/key';
import merge, { Merge } from './record/merge';
import omit, { Omit } from './record/omit';
import pick, { Pick } from './record/pick';
import shrink, { Shrink } from './record/shrink';

/* security */
import freeze, { Freeze } from './sec/freeze';
import digest, { Digest } from './sec/digest';
import uuid, { Uuid } from './sec/uuid';

/* string */
import capitalize, { Capitalize } from './str/capitalize';
import cut, { Cut } from './str/cut';
import form, { Form } from './str/form';
import format, { Format } from './str/format';
import join, { Join } from './str/join';
import list, { List } from './str/list';
import lowercase, { Lowercase } from './str/lowercase';
import match, { Match } from './str/match';
import matchAll, { MatchAll } from './str/match_all';
import path, { Path } from './str/path';
import parse, { Parse } from './str/parse';
import prefix, { Prefix } from './str/prefix';
import replace, { Replace } from './str/replace';
import searchIndex, { SearchIndex } from './str/search_index';
import searchValue, { SearchValue } from './str/search_value';
import search, { Search } from './str/search';
import slug, { Slug } from './str/slug';
import split, { Split } from './str/split';
import suffix, { Suffix } from './str/suffix';
import template, { Template } from './str/template';
import trim, { Trim } from './str/trim';
import truncate, { Truncate } from './str/truncate';
import uncapitalize, { Uncapitalize } from './str/uncapitalize';
import uppercase, { Uppercase } from './str/uppercase';
import word, { Word } from './str/word';

/* to */
import to_array, { ToArray } from './to/to_array';
import to_boolean, { ToBoolean } from './to/to_boolean';
import to_bounds, { ToBounds } from './to/to_bounds';
import to_false, { ToFalse } from './to/to_false';
import to_integer, { ToInteger } from './to/to_integer';
import to_real, { ToReal } from './to/to_real';
import to_record, { ToRecord } from './to/to_record';
import to_regexp, { ToRegExp } from './to/to_regexp';
import to_string, { ToString } from './to/to_string';
import to_true, { ToTrue } from './to/to_true';
import to_type, { ToType } from './to/to_type';

export interface F1 {
  /* adt */
  readonly compact: Compact;
  readonly each: Each;
  readonly every: Every;
  readonly filter: Filter;
  readonly find: Find;
  readonly keys: Keys;
  readonly map: Map;
  readonly group: Group;
  readonly partition: Partition;
  readonly reduce: Reduce;
  readonly remove: Remove;
  readonly size: Size;
  readonly some: Some;
  readonly traverse: Traverse;
  readonly source: Source;
  readonly unique: Unique;
  readonly unpartition: Unpartition;
  readonly ungroup: Ungroup;
  readonly until: Until;
  readonly values: Values;
  readonly iterable: Iterable;

  /* array */
  readonly bounds: Bounds;
  readonly chunk: Chunk;
  readonly concat: Concat;
  readonly dequeue: Dequeue;
  readonly difference: Difference;
  readonly enqueue: Enqueue;
  readonly flatten: Flatten;
  readonly head: Head;
  readonly initial: Initial;
  readonly intersection: Intersection;
  readonly last: Last;
  readonly nth: Nth;
  readonly pop: Pop;
  readonly push: Push;
  readonly range: Range;
  readonly reverse: Reverse;
  readonly segment: Segment;
  readonly slice: Slice;
  readonly sort: Sort;
  readonly tail: Tail;
  readonly union: Union;

  /* asynchronous */
  readonly parallel: Parallel;
  readonly serial: Serial;
  readonly tasks: Tasks;
  readonly task: Task;
  readonly done: Done;

  /* boolean */
  readonly and: And;
  readonly eq: Eq;
  readonly gt: Gt;
  readonly gte: Gte;
  readonly either: Either;
  readonly lt: Lt;
  readonly lte: Lte;
  readonly nand: Nand;
  readonly nor: Nor;
  readonly not: Not;
  readonly or: Or;
  readonly xor: Xor;

  /* database */
  readonly write: Write;
  readonly read: Read;
  readonly lens: Lens;

  /* function */
  readonly always: Always;
  readonly c2: Curry2;
  readonly c3: Curry3;
  readonly c4: Curry4;
  readonly c5: Curry5;
  readonly cN: CurryN;
  readonly compose: Compose;
  readonly conditional: Conditional;
  readonly converge: Converge;
  readonly debounce: Debounce;
  readonly delay: Delay;
  readonly identity: Identity;
  readonly lift: Lift;
  readonly memoize: Memoize;
  readonly method: Method;
  readonly mimic: Mimic;
  readonly noop: Noop;
  readonly once: Once;
  readonly pipe: Pipe;
  readonly safe: Safe;
  readonly skip: Skip;
  readonly strategy: Strategy;
  readonly tap: Tap;

  /* is */
  is: {
    readonly array: IsArray;
    readonly between: IsBetween;
    readonly bounded: IsBounded;
    readonly boolean: IsBoolean;
    readonly char: IsChar;
    readonly class: IsClass;
    readonly date: IsDate;
    readonly elements: IsElements;
    readonly empty: IsEmpty;
    readonly equal: IsEqual;
    readonly exists: IsExists;
    readonly equalDeep: IsEqualDeep;
    readonly error: IsError;
    readonly even: IsEven;
    readonly false: IsFalse;
    readonly falsy: IsFalsy;
    readonly function: IsFunction;
    readonly generator: IsGenerator;
    readonly in: IsIn;
    readonly integer: IsInteger;
    readonly iterable: IsIterable;
    readonly iterator: IsIterator;
    readonly json: IsJson;
    readonly like: IsLike;
    readonly lowercase: IsLowercase;
    readonly map: IsMap;
    readonly nan: IsNan;
    readonly negative: IsNegative;
    readonly nil: IsNil;
    readonly null: IsNull;
    readonly number: IsNumber;
    readonly object: IsObject;
    readonly odd: IsOdd;
    readonly pattern: IsPattern;
    readonly record: IsRecord;
    readonly positive: IsPositive;
    readonly private: IsPrivate;
    readonly protected: IsProtected;
    readonly public: IsPublic;
    readonly promise: IsPromise;
    readonly real: IsReal;
    readonly regexp: IsRegExp;
    readonly set: IsSet;
    readonly string: IsString;
    readonly similar: IsSimilar;
    readonly symbol: IsSymbol;
    readonly true: IsTrue;
    readonly truthy: IsTruthy;
    readonly undefined: IsUndefined;
    readonly uppercase: IsUppercase;
    readonly value: IsValue;
  };

  /* number */
  readonly add: Add;
  readonly ascending: Ascending;
  readonly ceiling: Ceiling;
  readonly clamp: Clamp;
  readonly dec: Dec;
  readonly digits: Digits;
  readonly divide: Divide;
  readonly fraction: Fraction;
  readonly inc: Inc;
  readonly max: Max;
  readonly min: Min;
  readonly modulo: Modulo;
  readonly multiply: Multiply;
  readonly precision: Precision;
  readonly random: Random;
  readonly sign: Sign;
  readonly subtract: Subtract;

  /* record */
  readonly assign: Assign;
  readonly clone: Clone;
  readonly copy: Copy;
  readonly merge: Merge;
  readonly key: Key;
  readonly omit: Omit;
  readonly pick: Pick;
  readonly shrink: Shrink;

  /* security */
  readonly freeze: Freeze;
  readonly digest: Digest;
  readonly uuid: Uuid;

  /* string */
  readonly capitalize: Capitalize;
  readonly form: Form;
  readonly format: Format;
  readonly suffix: Suffix;
  readonly prefix: Prefix;
  readonly parse: Parse;
  readonly matchAll: MatchAll;
  readonly path: Path;
  readonly match: Match;
  readonly searchIndex: SearchIndex;
  readonly searchValue: SearchValue;
  readonly search: Search;
  readonly slug: Slug;
  readonly join: Join;
  readonly list: List;
  readonly lowercase: Lowercase;
  readonly replace: Replace;
  readonly cut: Cut;
  readonly split: Split;
  readonly template: Template;
  readonly trim: Trim;
  readonly truncate: Truncate;
  readonly uncapitalize: Uncapitalize;
  readonly uppercase: Uppercase;
  readonly word: Word;

  /* to */
  to: {
    readonly array: ToArray;
    readonly boolean: ToBoolean;
    readonly bounds: ToBounds;
    readonly false: ToFalse;
    readonly integer: ToInteger;
    readonly real: ToReal;
    readonly record: ToRecord;
    readonly regExp: ToRegExp;
    readonly string: ToString;
    readonly true: ToTrue;
    readonly type: ToType;
  };
}

export * from './types';

/**
 *
 *
 * @public
 * @hidden
 */
const f1: F1 = freeze({
  /* adt */
  compact,
  each: c2(each) as Each,
  every: c2(every),
  filter: c2(filter) as Filter,
  find: c2(find),
  reduce: c3(reduce),
  remove: c2(remove) as Remove,
  group: c2(group),
  keys,
  size,
  map: c2(map) as Map,
  some: c2(some),
  unique,
  partition: c2(partition),
  traverse: c5(traverse) as Traverse,
  source: c3(source) as Source,
  unpartition,
  ungroup,
  until: c2(until) as Until,
  values,
  iterable,

  /* array */
  bounds,
  chunk: c2(chunk),
  concat: c2(concat),
  dequeue,
  difference: c2(difference),
  enqueue: c2(enqueue),
  flatten: c2(flatten),
  head,
  initial,
  intersection: c2(intersection),
  last,
  nth: c2(nth),
  pop,
  push: c2(push),
  range: c3(range),
  reverse,
  segment: c2(segment),
  slice: c3(slice),
  sort: c2(sort),
  tail,
  union: c2(union),

  /* asynchronous */
  parallel: c2(parallel),
  serial: c2(serial),
  tasks: c2(tasks) as Tasks,
  task: c2(task),
  done,

  /* boolean */
  and: c3(and),
  eq: c3(eq),
  gt: c3(gt),
  gte: c3(gte),
  either: c4(either),
  lt: c3(lt),
  lte: c3(lte),
  nand: c3(nand),
  nor: c3(nor),
  not: c2(not),
  or: c3(or),
  xor: c3(xor),

  /* db */
  write: c3(write),
  read: c3(read),
  lens,

  /* fn */
  always: c2(always),
  c2,
  c3,
  c4,
  c5,
  cN,
  compose: c3(compose),
  conditional: c3(conditional),
  converge: c4(converge),
  debounce: c2(debounce),
  delay,
  identity,
  lift,
  memoize,
  method: c3(method),
  mimic,
  noop,
  once,
  pipe: c2(pipe) as Pipe,
  safe: c2(safe),
  skip,
  strategy: c2(strategy),
  tap: c2(tap),

  /* is */
  is: freeze({
    array,
    between: c3(between),
    boolean,
    bounded: c2(bounded),
    char,
    class: is_class,
    date,
    elements: is_elements,
    empty,
    equal: c2(equal),
    exists,
    equalDeep: c2(equal_deep),
    error,
    even,
    false: is_false,
    falsy,
    function: is_function,
    generator: is_generator,
    in: c2(is_in) as IsIn,
    integer,
    iterable: is_iterable,
    iterator: is_iterator,
    json,
    like: c2(is_like),
    lowercase: is_lowercase,
    map: is_map,
    nan,
    negative,
    nil,
    null: is_null,
    number,
    object,
    odd,
    pattern: c2(pattern),
    record,
    positive,
    private: is_private,
    public: is_public,
    protected: is_protected,
    promise: is_promise,
    real,
    regexp,
    set: is_set,
    string: is_string,
    similar: c2(is_similar),
    symbol,
    true: is_true,
    truthy,
    undefined: is_undefined,
    uppercase: is_uppercase,
    value
  }),

  /* number */
  add: c2(add),
  ascending,
  ceiling,
  clamp: c3(clamp),
  dec,
  digits,
  divide: c2(divide),
  fraction: c2(fraction),
  inc,
  max: c2(max),
  min: c2(min),
  modulo: c2(modulo),
  multiply: c2(multiply),
  precision: c2(precision),
  random: c2(random),
  sign,
  subtract: c2(subtract),

  /* record */
  assign: c2(assign),
  omit: c2(omit),
  pick: c2(pick),
  merge: c3(merge),
  key: c2(key),
  copy,
  clone,
  shrink: c2(shrink),

  /* security */
  freeze,
  digest: c2(digest),
  uuid,

  /* string */
  capitalize,
  form: c3(form),
  format: c3(format),
  suffix: c2(suffix),
  prefix: c2(prefix),
  parse: c3(parse),
  matchAll: c2(matchAll),
  path,
  match: c2(match),
  searchIndex: c3(searchIndex),
  searchValue: c3(searchValue),
  search: c3(search),
  slug,
  join: c2(join),
  list,
  lowercase,
  replace: c3(replace),
  cut: c3(cut),
  split: c2(split),
  trim,
  template: c2(template),
  truncate: c2(truncate),
  uncapitalize,
  uppercase,
  word,

  /* to */
  to: freeze({
    array: to_array,
    boolean: to_boolean,
    bounds: c2(to_bounds),
    false: to_false,
    integer: to_integer,
    real: to_real,
    record: c2(to_record),
    regExp: to_regexp,
    string: to_string,
    true: to_true,
    type: to_type
  })
});

export default f1;
